import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Star, Shield, MapPin, Wrench, Clock, CheckCircle2,
  Award, ArrowLeft, Loader2, Plug, Droplets, HardHat,
  Phone, Mail, Building2, FileCheck, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";

const TRADE_META = {
  electrical: { label: "Electrician", icon: Plug, color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  plumbing: { label: "Plumber", icon: Droplets, color: "text-blue-400 bg-blue-500/10 border-blue-500/30" },
  general_contracting: { label: "General Contractor", icon: HardHat, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  construction: { label: "Construction", icon: Wrench, color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
};

const TIER_META = {
  bronze: { label: "Bronze", color: "text-amber-700 bg-amber-100 border-amber-300", star: "text-amber-700" },
  silver: { label: "Silver", color: "text-slate-600 bg-slate-100 border-slate-300", star: "text-slate-500" },
  gold: { label: "Gold", color: "text-amber-600 bg-amber-50 border-amber-300", star: "text-amber-500" },
  fortress: { label: "Fortress Certified", color: "text-cyan-700 bg-cyan-50 border-cyan-300", star: "text-cyan-600" },
};

export default function ContractorPublicProfile() {
  const { contractorId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contractor/public/${contractorId}`)
      .then(r => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then(d => setProfile(d.profile))
      .catch(() => setError("Contractor profile not found"))
      .finally(() => setLoading(false));
  }, [contractorId]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
    </div>
  );

  if (error || !profile) return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center px-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-2">Profile Not Found</h2>
        <p className="text-slate-400 mb-6">This contractor profile doesn&apos;t exist or has been removed.</p>
        <Link to="/"><Button className="bg-[#0056A8] hover:bg-[#003366] text-white">Back to Home</Button></Link>
      </div>
    </div>
  );

  const trade = TRADE_META[profile.trade] || TRADE_META.construction;
  const tier = TIER_META[profile.tier] || TIER_META.bronze;
  const TradeIcon = trade.icon;

  return (
    <div className="min-h-screen bg-[#0a0f1a]" data-testid="contractor-public-profile">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] via-[#0056A8] to-[#003366] border-b border-blue-500/20">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-200 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Network
          </Link>
          <div className="flex items-start gap-6">
            <div className={`w-20 h-20 rounded-xl border ${trade.color} flex items-center justify-center flex-shrink-0`}>
              <TradeIcon className={`w-10 h-10 ${trade.color.split(" ")[0]}`} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-black text-white mb-1" data-testid="profile-business-name">{profile.business_name}</h1>
              <p className="text-blue-200">{trade.label} — {profile.service_area}</p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${tier.color}`}>
                  <Star className={`w-3.5 h-3.5 ${tier.star}`} />
                  {tier.label}
                </span>
                {profile.fortress_certified && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                    <Shield className="w-3.5 h-3.5" /> Fortress Certified
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80 border border-white/20">
                  <MapPin className="w-3 h-3" /> {profile.city}, {profile.state}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="profile-stats">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-center">
            <p className="text-2xl font-black text-cyan-400">{profile.total_jobs_completed}</p>
            <p className="text-xs text-slate-400 mt-1">Jobs Completed</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-center">
            <p className="text-2xl font-black text-amber-400">{profile.rating > 0 ? `${profile.rating}/5` : "New"}</p>
            <p className="text-xs text-slate-400 mt-1">Rating</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-center">
            <p className="text-2xl font-black text-emerald-400">{profile.years_experience || "—"}</p>
            <p className="text-xs text-slate-400 mt-1">Years Experience</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-center">
            <p className="text-2xl font-black text-white capitalize">{profile.tier}</p>
            <p className="text-xs text-slate-400 mt-1">Network Tier</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Details */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Contractor Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3"><Wrench className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-300">Trade: <strong className="text-white capitalize">{profile.trade?.replace(/_/g, " ")}</strong></span></div>
              <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-300">Service Area: <strong className="text-white">{profile.service_area}</strong></span></div>
              {profile.license_number && <div className="flex items-center gap-3"><FileCheck className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-300">License: <strong className="text-white">{profile.license_number}</strong></span></div>}
              <div className="flex items-center gap-3"><Calendar className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-300">Member Since: <strong className="text-white">{profile.member_since ? new Date(profile.member_since).getFullYear() : "2024"}</strong></span></div>
            </div>
          </div>

          {/* Credentials */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Credentials & Certifications</h3>
            {(profile.credentials || []).length === 0 ? (
              <p className="text-sm text-slate-500">No credentials uploaded yet.</p>
            ) : (
              <div className="space-y-2">
                {profile.credentials.map((cred, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <CheckCircle2 className={`w-4 h-4 ${cred.status === "verified" ? "text-emerald-400" : "text-amber-400"}`} />
                    <span className="text-sm text-white">{cred.name}</span>
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${cred.status === "verified" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{cred.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tier Progress */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Network Tier Progress</h3>
          <div className="flex items-center gap-2">
            {["bronze", "silver", "gold", "fortress"].map((t, idx) => {
              const meta = TIER_META[t];
              const isActive = t === profile.tier;
              const isPast = ["bronze", "silver", "gold", "fortress"].indexOf(profile.tier) >= idx;
              return (
                <div key={t} className="flex-1 flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${isActive ? "border-cyan-400 bg-cyan-500/20" : isPast ? "border-emerald-400 bg-emerald-500/10" : "border-slate-700 bg-slate-800"}`}>
                    <Star className={`w-5 h-5 ${isActive ? "text-cyan-400" : isPast ? "text-emerald-400" : "text-slate-600"}`} />
                  </div>
                  <p className={`text-xs mt-2 font-semibold ${isActive ? "text-cyan-400" : isPast ? "text-emerald-400" : "text-slate-600"}`}>{meta.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-6 border-t border-slate-800">
          <p className="text-slate-400 text-sm mb-4">Want to work with contractors like {profile.business_name}?</p>
          <div className="flex gap-3 justify-center">
            <Link to="/join-network"><Button className="bg-emerald-600 hover:bg-emerald-700 text-white">Join the Network</Button></Link>
            <Link to="/"><Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800">Back to Home</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
